import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Paper,
  useMediaQuery,
  Divider,
  Alert,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import UserPageLayout from '../../components/user/UserPageLayout';
import { Person, Phone, Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';

const GuestSignupPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const taxDetails = location.state;
  console.log("location.state => " , location.state)
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const serviceType = location.state?.serviceType || 'delivery';
  
  console.log("serviceType => ", serviceType);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!form.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[89]\d{7}$/.test(form.phone.trim())) {
      newErrors.phone = 'Please enter a valid Singapore phone number (8 digits starting with 8 or 9)';
    }
    
    if (isSignup) {
      if (!form.email.trim()) {
        newErrors.email = 'Email is required for signup';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
      
      if (!form.password.trim()) {
        newErrors.password = 'Password is required';
      } else if (form.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      // Store user info in sessionStorage for checkout
      sessionStorage.setItem('userInfo', JSON.stringify({
        name: form.name,
        phone: form.phone,
        email: isSignup ? form.email : '',
        isGuest: !isSignup
      }));
      
      // Navigate to checkout with user info
      navigate('/checkout', { 
        state: { 
          serviceType,
          userInfo: {
            name: form.name,
            phone: form.phone,
            email: isSignup ? form.email : '',
            isGuest: !isSignup
          },
          taxDetails:taxDetails
        } 
      });
    }
  };

  const handleSignup = async () => {
    if (validateForm()) {
      try {
        // Here you would call your signup API
        // For now, we'll just store the user info and continue
        sessionStorage.setItem('userInfo', JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          isGuest: false
        }));
        
        navigate('/checkout', { 
          state: { 
            serviceType,
            userInfo: {
              name: form.name,
              phone: form.phone,
              email: form.email,
              isGuest: false
            }
          } 
        });
      } catch (error) {
        console.error('Signup error:', error);
        setErrors({ general: 'Signup failed. Please try again.' });
      }
    }
  };

  return (
    <UserPageLayout 
      title={isSignup ? "Create Account" : "Continue as Guest"} 
      subtitle={isSignup ? "Sign up for faster checkout and order tracking" : "Quick checkout without creating an account"}
    >
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
        {/* Service Type Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 2, 
              bgcolor: serviceType === 'delivery' ? 'success.light' : 'warning.light', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {serviceType === 'delivery' ? '🚚' : '🛍️'}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {serviceType === 'delivery' ? 'Delivery Order' : 'Takeaway Order'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {serviceType === 'delivery' ? 'We\'ll deliver to your address' : 'Ready for pickup at the outlet'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Guest/Signup Toggle */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              fullWidth
              variant={!isSignup ? 'contained' : 'outlined'}
              onClick={() => setIsSignup(false)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 600,
                height: { xs: 48, sm: 52 }
              }}
            >
              Continue as Guest
            </Button>
            <Button
              fullWidth
              variant={isSignup ? 'contained' : 'outlined'}
              onClick={() => setIsSignup(true)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 600,
                height: { xs: 48, sm: 52 }
              }}
            >
              Create Account
            </Button>
          </Box>

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiInputBase-root': {
                    height: { xs: 56, sm: 60 }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Singapore Phone Number *"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                error={!!errors.phone}
                helperText={errors.phone || 'Enter 8-digit Singapore phone number (e.g., 81234567)'}
                placeholder="81234567"
                inputProps={{ maxLength: 8 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  '& .MuiInputBase-root': {
                    height: { xs: 56, sm: 60 }
                  }
                }}
              />
            </Grid>

            {isSignup && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address *"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiInputBase-root': {
                        height: { xs: 56, sm: 60 }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password *"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ minWidth: 'auto', p: 1 }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiInputBase-root': {
                        height: { xs: 56, sm: 60 }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password *"
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiInputBase-root': {
                        height: { xs: 56, sm: 60 }
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 600,
                height: { xs: 48, sm: 52 }
              }}
            >
              Back to Cart
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={isSignup ? handleSignup : handleContinue}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 600,
                height: { xs: 48, sm: 52 }
              }}
            >
              {isSignup ? 'Create Account & Continue' : 'Continue as Guest'}
            </Button>
          </Box>

          {!isSignup && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
              <Typography variant="body2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                💡 Guest Checkout Benefits:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • Quick checkout without creating an account<br/>
                • Your phone number will be used for order updates<br/>
                • You can create an account later for order history
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </UserPageLayout>
  );
};

export default GuestSignupPage;
